import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { KeyRound, Trash2, X } from 'lucide-react';

import CadastroFacialModal from '../pages/Abertura/CadastroFacialModal';
import api, { getApiErrorMessage } from '../services/api';
import { hasPermission } from '../utils/permissions';

function DestinationModal({
  mode,
  dispositivos,
  grupos,
  selectedTipo,
  selectedId,
  onTipoChange,
  onIdChange,
  onClose,
  onConfirm,
  loading,
}) {
  const isRemove = mode === 'remove';
  const buttonClass = isRemove ? 'bg-red-700 hover:bg-red-800' : 'bg-slate-900 hover:bg-slate-800';

  return createPortal(
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b bg-gray-50/50 px-5 py-4">
          <h2 className="text-lg font-bold text-slate-800">
            {isRemove ? 'Remover acesso facial' : 'Criar acesso facial'}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-red-500">
            <X size={22} />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="grid grid-cols-1 gap-3">
            <button
              type="button"
              onClick={() => onTipoChange('grupo')}
              className={`rounded-xl border px-4 py-3 text-left text-sm font-semibold transition ${
                selectedTipo === 'grupo'
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-gray-300 bg-white text-slate-700 hover:bg-slate-50'
              }`}
            >
              Perfil do usuário
            </button>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-gray-700">
              {selectedTipo === 'grupo' ? 'Grupo facial' : 'Facial'}
            </label>
            <select
              className="w-full rounded-xl border border-gray-300 bg-white p-3 outline-none"
              value={selectedId}
              onChange={(event) => onIdChange(event.target.value)}
            >
              <option value="">Selecione...</option>
              {selectedTipo === 'grupo'
                ? grupos.map((grupo) => (
                    <option key={grupo.id} value={grupo.id}>
                      {grupo.nome} - {grupo.total_dispositivos || 0} facial(is)
                    </option>
                  ))
                : dispositivos.map((dispositivo) => (
                    <option key={dispositivo.id} value={dispositivo.id}>
                      {dispositivo.nome} - {dispositivo.marca || 'sem marca'} - {dispositivo.ip}
                    </option>
                  ))}
            </select>
          </div>

          <button
            type="button"
            disabled={!selectedId || loading}
            onClick={onConfirm}
            className={`w-full rounded-xl py-3 text-sm font-bold text-white transition disabled:cursor-not-allowed disabled:opacity-50 ${buttonClass}`}
          >
            {loading ? 'Processando...' : isRemove ? 'Remover acesso' : 'Continuar'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function getStoredAuth() {
  if (typeof window === 'undefined') return null;
  try {
    const userData = localStorage.getItem('authUserData');
    const permissionsData = localStorage.getItem('authPermissions');
    return {
      user: userData ? JSON.parse(userData) : null,
      permissions: permissionsData ? JSON.parse(permissionsData) : {},
    };
  } catch {
    return null;
  }
}

export default function FacialAccessActions({ pessoa }) {
  const auth = getStoredAuth();
  const canUseFacial = ['criar', 'editar', 'excluir'].some((acao) => hasPermission(auth, 'abertura', acao)) || hasPermission(auth, 'dispositivos', 'visualizar');
  const [dispositivos, setDispositivos] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [modalMode, setModalMode] = useState(null);
  const [tipoDestino, setTipoDestino] = useState('grupo');
  const [selectedId, setSelectedId] = useState('');
  const [cadastroOpen, setCadastroOpen] = useState(false);
  const [removing, setRemoving] = useState(false);

  const pessoaValida = Boolean(pessoa?.tipo && pessoa?.id);

  useEffect(() => {
    if (!pessoaValida || !canUseFacial) return undefined;

    let ativo = true;
    setLoadingOptions(true);
    Promise.all([api.get('/dispositivos/'), api.get('/dispositivos/grupos')])
      .then(([dispositivosResponse, gruposResponse]) => {
        if (!ativo) return;
        setDispositivos(dispositivosResponse.data || []);
        setGrupos(gruposResponse.data || []);
      })
      .catch((error) => {
        if (error?.response?.status !== 403) {
          alert(getApiErrorMessage(error, 'Erro ao carregar faciais e grupos.'));
        }
      })
      .finally(() => {
        if (ativo) setLoadingOptions(false);
      });

    return () => {
      ativo = false;
    };
  }, [pessoaValida, canUseFacial]);

  const selectedDispositivo = dispositivos.find((item) => String(item.id) === String(selectedId));
  const selectedGrupo = grupos.find((item) => String(item.id) === String(selectedId));
  const dispositivosDoGrupo = useMemo(
    () => dispositivos.filter((dispositivo) => dispositivo.grupo_ids?.includes(Number(selectedId))),
    [dispositivos, selectedId]
  );
  const dispositivosSelecionados = tipoDestino === 'grupo'
    ? dispositivosDoGrupo
    : selectedDispositivo
      ? [selectedDispositivo]
      : [];

  if (!canUseFacial) {
    return null;
  }

  const abrirModal = (mode) => {
    if (!pessoaValida) {
      alert('Salve o cadastro antes de criar ou remover acesso facial.');
      return;
    }

    setModalMode(mode);
    setTipoDestino('grupo');
    setSelectedId('');
  };

  const confirmarCriacao = () => {
    if (!dispositivosSelecionados.length) {
      alert('Selecione um grupo com faciais vinculadas.');
      return;
    }

    setModalMode(null);
    setCadastroOpen(true);
  };

  const confirmarRemocao = async () => {
    if (!dispositivosSelecionados.length || removing) return;

    const destino = tipoDestino === 'grupo'
      ? `${dispositivosSelecionados.length} facial(is) do grupo ${selectedGrupo?.nome || ''}`
      : selectedDispositivo?.nome || 'facial selecionada';

    if (!window.confirm(`Remover o acesso de ${pessoa.nome} em ${destino}?`)) return;

    setRemoving(true);
    try {
      const response = await api.post('/dispositivos/usuarios/remover', {
        usuario_tipo: pessoa.tipo,
        usuario_id: pessoa.id,
        dispositivo_ids: dispositivosSelecionados.map((dispositivo) => dispositivo.id),
      }, {
        validateStatus: (status) => status >= 200 && status < 300,
      });

      const sucessos = response.data?.sucessos?.length || 0;
      const falhas = response.data?.falhas?.length || 0;
      const mensagem = sucessos || falhas
        ? `Removido de ${sucessos} facial(is)` + (falhas ? `, falha em ${falhas}.` : '.')
        : response.data?.message || 'Acesso removido com sucesso.';
      alert(mensagem);
      setModalMode(null);
    } catch (error) {
      alert(getApiErrorMessage(error, 'Erro ao remover acesso.'));
    } finally {
      setRemoving(false);
    }
  };


  return (
    <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <button
          type="button"
          disabled={loadingOptions}
          onClick={() => abrirModal('create')}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-bold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <KeyRound size={18} />
          Criar acesso
        </button>
        <button
          type="button"
          disabled={loadingOptions}
          onClick={() => abrirModal('remove')}
          className="inline-flex items-center justify-center gap-2 rounded-xl bg-red-700 px-4 py-3 text-sm font-bold text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Trash2 size={18} />
          Remover acesso
        </button>
      </div>

      {modalMode && (
        <DestinationModal
          mode={modalMode}
          dispositivos={dispositivos}
          grupos={grupos}
          selectedTipo={tipoDestino}
          selectedId={selectedId}
          onTipoChange={(tipo) => {
            setTipoDestino(tipo);
            setSelectedId('');
          }}
          onIdChange={setSelectedId}
          onClose={() => setModalMode(null)}
          onConfirm={modalMode === 'remove' ? confirmarRemocao : confirmarCriacao}
          loading={removing}
        />
      )}

      {cadastroOpen && createPortal(
        <CadastroFacialModal
          isOpen={cadastroOpen}
          onClose={() => setCadastroOpen(false)}
          dispositivos={dispositivosSelecionados}
          grupo={tipoDestino === 'grupo' ? selectedGrupo : null}
          moradores={[]}
          visitantes={[]}
          contratados={[]}
          usuarioPreSelecionado={pessoa}
        />,
        document.body
      )}
    </div>
  );
}
